import React, { useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@common/reducers/hooks/store.hook';
import { campaignsSelectOneObjectByQuery } from '@common/reducers/DBServiceThunks';
import { getNumberOfContactsByCampaignIdThunk, getCampaignRealStatus } from '@common/reducers/CampaignThunks';
import CampaignRowMenu from '@extension/context/content/components/bt-bulk-dashboard/campaign-row-menu';
import { Box, ButtonBase, Tooltip } from '@mui/material';
import { Campaign as CampaignIcon } from "@mui/icons-material";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { InProgressCampaignIndicator } from '@extension/context/content/components/bt-bulk-dashboard/in-progress-campaign-indicator';
import { getTaskSumByBoardIdRecord } from '@common-reducers/CampaignSelectors';
import moment from 'moment';
import { StatusBadge } from '@extension/context/content/components/bt-bulk-dashboard/status-labels';
import { RealCampaignStatus } from '@extension/context/content/components/bt-bulk-dashboard/campaign-dialog-row';
import { Stack } from '@mui/material';



type Props = {
    index: number;
    campaignId: string;
}

export default function CalendarSidebarCampaignRow({ index, campaignId }: Props) {
    const dispatch = useAppDispatch();

    const campaign = useAppSelector(state => campaignsSelectOneObjectByQuery(state.DBCampaignReducer, { _id: campaignId }))
    const taskSumByBoardIdRecord = useAppSelector(getTaskSumByBoardIdRecord);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const rowRef = useRef<HTMLDivElement>(null);

    const realStatus = getCampaignRealStatus(campaign?.campaignStatus, !!campaign?.contactCount) as RealCampaignStatus;
    // Track if the menu is open
    const isMenuOpen = Boolean(anchorEl);

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleOpenMenu = (event: React.MouseEvent) => {
        if (!isMenuOpen && rowRef.current) {
            setAnchorEl(rowRef.current);
        }
    };

    useEffect(() => {
        if (campaign) {
            dispatch(getNumberOfContactsByCampaignIdThunk(campaign?._id))
        }
    }, [campaign, dispatch]);

    // Calculate total number of contacts
    const totalNumberOfContacts = () => {
        if (campaign?.contactCount) {
            return campaign?.contactCount
        }
        else if (campaign?.list && campaign?.list?.contactIdList) {
            return campaign?.list?.contactIdList?.length
        }
        else {
            return (campaign?.boardIDArray?.reduce((sum, id) => sum + taskSumByBoardIdRecord[id], 0) || 0)
        }
    };

    // Get formatted date strings
    const formattedDueDate = campaign?.schedule?.dueDate
        ? moment(campaign.schedule.dueDate).format("HH:mm DD/MM/YYYY")
        : "";

    // Get campaign message from template
    const campaignMessage = campaign?.template?.message || "";

    // Custom tooltip content
    const TooltipContent = () => (
        <Box
            sx={{
                padding: 1.5,
                maxWidth: 300,
                wordBreak: 'break-word',
            }}
        >
            <Box sx={{ mb: 2 }}>
                {campaignMessage || '\u00A0'}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    fontSize: '12px',
                    fontWeight: 500,
                }}
            >
                {formattedDueDate}
            </Box>
        </Box>
    );

    return (
        <div ref={rowRef}>
            <ButtonBase
                key={`campaign${index}`}
                onClick={handleOpenMenu}
                sx={{
                    width: '100%',
                    display: 'block',
                    textAlign: 'left',
                    borderBottom: '1px solid var(--border-list)',
                    transition: 'background-color 0.3s',
                    '&:hover': {
                        backgroundColor: 'var(--background-default-hover)',
                    }
                }}
            >
                <Tooltip
                    title={<TooltipContent />}
                    arrow
                    placement="top"
                    enterDelay={500}
                    leaveDelay={200}
                >
                    <Stack sx={{
                        padding: "16px 11px",
                        width: "100%",
                        boxSizing: 'border-box',
                        gap: '10px',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: 1.5,
                        }}>
                            {/* Campaign Icon */}
                            <Box sx={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'var(--campaign-status-running-bg)',
                                flexShrink: 0,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                            }}>
                                <CampaignIcon
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        color: 'var(--campaign-status-running-text)',
                                    }}
                                />
                            </Box>

                            {/* Content Area */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: 'calc(100% - 56px)', // Account for icon width and gap
                                gap: 1,
                                overflow: 'hidden' // Add overflow hidden to prevent content from expanding
                            }}>
                                {/* First row: Campaign message and date badge */}
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%'
                                }}>
                                    {/* Campaign message */}
                                    <Box sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: 'var(--text-color)',
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        flexGrow: 1,
                                        mr: 1
                                    }}>
                                        {campaignMessage || campaign?.name || 'Unnamed Campaign'}
                                    </Box>
                                </Box>

                                {/* Number of contacts */}
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%'
                                }}>
                                    {/* Campaign message */}
                                    <Box sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: 'var(--text-color)',
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        flexGrow: 1,
                                        mr: 1
                                    }}>
                                        {totalNumberOfContacts()} contacts
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%'
                        }}>
                            {/* Status indicator or Progress */}
                            <>
                                {campaign?.campaignStatus === 'running' &&
                                    <InProgressCampaignIndicator
                                        key={campaign?._id}
                                        campaignId={campaign?._id}
                                        showProgressBar={true}
                                        onClick={() => { }}
                                    />
                                }
                                {
                                    campaign?.campaignStatus === 'pending' &&
                                    StatusBadge(realStatus)
                                }

                                {/* Move date badge to first row */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: 'var(--campaign-status-running-bg)',
                                        borderRadius: '12px',
                                        padding: '2px 8px',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        color: 'var(--campaign-status-running-text)',
                                        border: '1px solid rgba(76, 175, 80, 0.2)',
                                        height: '22px',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                        '& svg': {
                                            fontSize: '13px',
                                            marginRight: '4px',
                                            color: 'var(--campaign-status-running-text)'
                                        }
                                    }}
                                >
                                    <AccessTimeIcon fontSize="inherit" />
                                    {formattedDueDate}
                                </Box>
                            </>
                        </Box>
                    </Stack>
                </Tooltip>

                {/* Menu */}
                <CampaignRowMenu
                    campaignId={campaign?._id}
                    handleCloseMenu={handleCloseMenu}
                    anchorEl={isMenuOpen ? rowRef.current : null}
                    shouldShowViewLogs={false}
                    isCampaignDuplicateShow={false}
                    isCampaignSummaryShow={false}
                />
            </ButtonBase>
        </div>
    )
}